import { useState, useRef } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { flushSync } from 'react-dom';
import { CSVLink } from 'react-csv';
import { getFromNow } from 'utils/date';
import { PROJECT_PERMISSIONS } from 'utils/permissions';
import { SemanticUI } from '@iterable/deprecated_react_components';
import { Button, BUTTON_VARIANTS, IconButton } from 'ui/button';
import { Delete16 } from 'ui/icons';
import { spacing } from 'ui/theme';
import { Tooltip } from 'ui/tooltip';
import { PageHeader } from 'ui/pageHeader';
import { PageHeaderInlineEdit } from 'ui/inlineEdit';
import {
  selectDoesUserHaveRbacPermission,
  selectIsExportReportsAsCsvFeatureFlagOn,
} from 'core/userContext/selectors';
import { openDeleteReportModal } from 'modules/reportsManager';
import { getActiveReport, getReportHasUnsavedChanges } from '../selectors';
import {
  updateReportFilters,
  saveReport,
  exportReportDataAsCsvRequest,
  saveAndExportReportDataAsCsvRequest,
} from '../actions';
import { ExportReportModal } from './ExportReportModal';

const StyledListItem = styled(SemanticUI.List.Item)`
  &&&&& {
    line-height: 1;
  }
`;

const DisplayFlex = styled.div`
  display: flex;
  gap: ${spacing.quarter};
`;

const HiddenCSVLink = styled(CSVLink)`
  display: none;
`;

const InlineEditContainer = styled.div`
  max-width: 40ch;
`;

export const ReportControls = () => {
  const dispatch = useDispatch();
  const [isExportReportModalOpen, setIsExportReportModalOpen] = useState(false);

  const report = useSelector(getActiveReport);
  const canManageReports = useSelector(selectDoesUserHaveRbacPermission)(
    PROJECT_PERMISSIONS.ManageReports,
  );
  const isExportReportsAsCsvOn = useSelector(selectIsExportReportsAsCsvFeatureFlagOn);
  const hasUnsavedChanges = useSelector(getReportHasUnsavedChanges);

  const { createdAt, creatorUserId, updatedAt, id, name, saving } = report;
  const formattedCreatedAt = getFromNow(createdAt);
  const formattedUpdatedAt = getFromNow(updatedAt);
  const csvLink = useRef();

  const [displayName, setDisplayName] = useState(name);
  const [csvData, setCsvData] = useState('');

  const updateReportName = (value) => {
    setDisplayName(value);
  };

  const updateReportFiltersWithName = () => {
    dispatch(updateReportFilters({ name: displayName, id }));
  };

  // Callback to mimick the button click for CSVLink
  // This callback is called after the csv data is fetched, so that the data prop in the CSVLink is updated
  const clickExportCsvButtonCallback = (data) => {
    flushSync(() => {
      setCsvData(data);
    });

    csvLink.current.link.click();
  };

  const handleExportReport = () => {
    dispatch(exportReportDataAsCsvRequest(report.id, clickExportCsvButtonCallback));
  };

  const handleClickExportReportButton = () => {
    return hasUnsavedChanges ? setIsExportReportModalOpen(true) : handleExportReport();
  };

  const handleSaveAndExportReport = () => {
    dispatch(saveAndExportReportDataAsCsvRequest(report, clickExportCsvButtonCallback));
  };

  const content = (
    <DisplayFlex>
      <Tooltip
        isEnabled={!canManageReports}
        tooltipContent="You do not have permissions to create and manage reports. A project administrator could
        change this."
      >
        <Button
          id="save-report"
          data-test="saveReportButton"
          onClick={() => dispatch(saveReport(report))}
          isDisabled={!canManageReports}
          isLoading={saving}
        >
          Save report
        </Button>
      </Tooltip>

      {isExportReportsAsCsvOn && (
        <>
          <Button
            data-test="exportReportButton"
            onClick={handleClickExportReportButton}
            variant={BUTTON_VARIANTS.SECONDARY}
          >
            Export to CSV
          </Button>
          {/* CSVLink doesn't allow to trigger a csv download after an api call, that's why we hide it and mimick the click in order
      to trigger the download after fetching the data. For more detail; https://github.com/react-csv/react-csv/issues/72#issuecomment-421160842 */}
          <HiddenCSVLink
            data={csvData}
            filename={`${report.name}.csv`}
            ref={csvLink}
            target="_blank"
          />
        </>
      )}

      <Tooltip
        isEnabled={!canManageReports}
        tooltipContent="You do not have permissions to create and manage reports. A project administrator could
        change this."
      >
        <IconButton
          id="delete-report"
          data-test="deleteReportButton"
          isDisabled={!canManageReports}
          variant={BUTTON_VARIANTS.SECONDARY}
          onClick={() => dispatch(openDeleteReportModal())}
        >
          <Delete16 />
        </IconButton>
      </Tooltip>
    </DisplayFlex>
  );

  return (
    <>
      <PageHeader
        breadcrumb={{
          content: 'Reports',
          href: '/reports',
        }}
        pageSubSection={
          <SemanticUI.List divided horizontal size={'tiny'}>
            <StyledListItem data-test="reportCreatorInfo">
              created by {creatorUserId} {formattedCreatedAt}
            </StyledListItem>
            <StyledListItem data-test="reportTimeUpdatedInfo">
              last updated {formattedUpdatedAt}
            </StyledListItem>
          </SemanticUI.List>
        }
        pageActions={content}
      >
        <InlineEditContainer>
          <PageHeaderInlineEdit
            value={displayName}
            onChange={updateReportName}
            onBlur={updateReportFiltersWithName}
          />
        </InlineEditContainer>
      </PageHeader>
      {isExportReportsAsCsvOn && (
        <ExportReportModal
          isOpen={isExportReportModalOpen}
          closeModal={() => setIsExportReportModalOpen(false)}
          csvExportData={csvData}
          exportAction={handleExportReport}
          saveAndExportAction={handleSaveAndExportReport}
        />
      )}
    </>
  );
};
